import React, { useCallback, useEffect, useReducer, useRef } from 'react'
import { DomainNotificationTemplateProviderContext, domainNotificationTemplateProviderReducer } from './internal'
import { GetNotificationResponse } from '@pushly/models/lib/structs/notifications'
import { parseNotificationResponse } from '../../features/notifications/helpers/notification-helpers'
import { useService } from '@pushly/aqe/lib/hooks'
import { CustomApiResponseErrorHandler } from '../../types/api-call-options-with-custom-error-handler'
import { NotificationTemplateService } from '../../services/notification-template'
import { NotificationDeliveryType } from '../../enums/notification-delivery-type'
import { NotificationDeliveryWindow } from '../../enums/notification-delivery-window'

type NotificationProviderProps = {
    domainId: number
    templateId?: string | null
    onFetchError?: CustomApiResponseErrorHandler
    children: React.ReactNode
}

export const DomainNotificationTemplateProvider = (props: NotificationProviderProps) => {
    const { domainId, onFetchError, children } = props

    const propsTemplateId: number | null = 'templateId' in props && props.templateId ? Number(props.templateId) : null

    const tmplSvc = useService(NotificationTemplateService)

    const [state, dispatch] = useReducer(domainNotificationTemplateProviderReducer, {
        domainId,
        loading: !!props.templateId,
        templateId: props.templateId ?? null,
        template: null,
        notifications: [],
    })

    // prevents duplicate load effects without triggering re-renders
    const localLoaderInitializedRef = useRef(false)

    const loadRemoteNotification = useCallback(async () => {
        const canLoadRemote = !localLoaderInitializedRef.current || domainId !== state.domainId
        if (!canLoadRemote) return

        if (!propsTemplateId) return

        if (!localLoaderInitializedRef.current) localLoaderInitializedRef.current = true
        if (!state.loading) dispatch({ type: 'loading:set', loading: true })

        const notifications: GetNotificationResponse[] = []

        const { data: template } = await tmplSvc.fetchNamedNotificationTemplateById(domainId, propsTemplateId, {
            errorHandler: onFetchError,
        })

        if (template) {
            const {
                name,
                description,
                templateContent,
                templateSegmentIds,
                templateExcludedSegmentIds,
                templateTtlSpec,
            } = template

            const notification = parseNotificationResponse({
                delivery_spec: {
                    type: NotificationDeliveryType.IMMEDIATE,
                    window: NotificationDeliveryWindow.STANDARD,
                    ...templateTtlSpec,
                },
                audience: {
                    segment_ids: templateSegmentIds?.length ? Array.from(templateSegmentIds) : [],
                    excluded_segment_ids: templateExcludedSegmentIds?.length
                        ? Array.from(templateExcludedSegmentIds)
                        : undefined,
                },
                template: {
                    channels: {
                        default: {
                            ...templateContent.default,
                            keywords: template.keywords,
                            domain_id: domainId,
                        },
                        web: templateContent.web,
                        native_ios: templateContent.nativeIos,
                        native_android: templateContent.nativeAndroid,
                    },
                    keywords: template.keywords?.length ? Array.from(template.keywords) : [],
                    name,
                    description,
                },
                channels: Array.from(templateContent.default.channels),
            })

            notifications.push(notification)
        }

        if (template && notifications.length) {
            dispatch({ type: 'notifications:set', notifications, template })
        }

        dispatch({ type: 'loading:set', loading: false })
    }, [domainId, propsTemplateId])

    useEffect(() => {
        loadRemoteNotification()
    }, [loadRemoteNotification])

    return (
        <DomainNotificationTemplateProviderContext.Provider value={{ state, dispatch }}>
            {children}
        </DomainNotificationTemplateProviderContext.Provider>
    )
}
